import React, {useCallback, useEffect, useRef, useState} from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import * as context from 'Context';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import {ACTION_SYNC_FILE} from 'notebook/store/VersioningConstants';
import TFIActions from 'notebook/tfi/TFIActions';
import {TFIFileMeta, TFIFileMetaState} from 'notebook/tfi/TFIConstants';
import {isCurrentUserLocalSyncFile} from 'notebook/tfi/TFIFileSync';
import {FromServerNoteLinkedFile, NoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import uniqueId from 'util/uniqueId';

export interface UseTFIFileSyncArgs {
  /** The attachment's TFIFileMeta, as returned by `getTFIFileMeta`. */
  tfiFileMeta: TFIFileMeta | null;
  fileName: string | undefined;
  tfiFileMetaState: TFIFileMetaState | null;
  locked: boolean;
  setUpdatedNoteLinkedFile: (noteLinkedFile: NoteLinkedFile | FromServerNoteLinkedFile) => void;
}

export interface UseTFIFileSyncReturns {
  /** Whether the upstream third-party file has a newer revision than the attached copy. */
  hasUpstreamTFIChanges: boolean;
  /** Whether a check for upstream changes is currently in flight. */
  isCheckingForUpdates: boolean;
  /** Pulls the upstream revision into the attachment, if there is one and no update check is in flight. */
  syncTFIFile: (e?: React.SyntheticEvent | null) => void;
}

function hasUpstreamChanges(
  tfiFileMeta: TFIFileMeta | null,
  tfiFileMetaState: TFIFileMetaState | null
): boolean {
  if (!tfiFileMetaState || !tfiFileMetaState.upstreamThirdpartyFileRev) {
    return false;
  }
  return tfiFileMetaState.upstreamThirdpartyFileRev !== tfiFileMeta!.thirdpartyFileRev;
}

/**
 * Keeps an attachment backed by a third-party file (TFI) in sync with its upstream file.
 *
 * While mounted, the attachment's TFIFileMeta is registered for periodic update checks. When upstream changes
 * are detected while the attachment is mounted, they are pulled in automatically; otherwise, the user is
 * notified with a toast offering to sync.
 */
export default function useTFIFileSync({
  tfiFileMeta,
  fileName,
  tfiFileMetaState,
  locked,
  setUpdatedNoteLinkedFile,
}: UseTFIFileSyncArgs): UseTFIFileSyncReturns {
  const tfiFileMetaId = tfiFileMeta?.id ?? null;
  const upstreamRev = tfiFileMetaState?.upstreamThirdpartyFileRev ?? null;
  const hasUpstreamTFIChanges = hasUpstreamChanges(tfiFileMeta, tfiFileMetaState);
  const isCheckingForUpdates = Boolean(tfiFileMetaState?.checkingForUpdates);

  const [tfiNotificationId] = useState(() => uniqueId('tfiNotificationId'));
  const isMountedRef = useRef(false);
  const latestRef = useRef({fileName, tfiFileMeta, tfiFileMetaState, setUpdatedNoteLinkedFile});
  latestRef.current = {fileName, tfiFileMeta, tfiFileMetaState, setUpdatedNoteLinkedFile};

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      _.defer(() => {
        ToastActions.closeToast(tfiNotificationId);
      });
    };
  }, [tfiNotificationId]);

  const syncTFIFile = useCallback((e: React.SyntheticEvent | null = null) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const latest = latestRef.current;
    if (!hasUpstreamChanges(latest.tfiFileMeta, latest.tfiFileMetaState)) {
      return;
    }
    if (latest.tfiFileMetaState?.checkingForUpdates) {
      return;
    }
    TFIActions.syncFile(latest.tfiFileMeta!, latest.fileName!, {
      onSuccess: (noteLinkedFile) => {
        if (isMountedRef.current) {
          context.using({immediateAutoSaverQueueSaveAction: ACTION_SYNC_FILE}, null, () => {
            latestRef.current.setUpdatedNoteLinkedFile(noteLinkedFile);
          });
        }
      },
    });
  }, []);

  useEffect(() => {
    if (tfiFileMetaId == null) {
      return undefined;
    }
    if (isCurrentUserLocalSyncFile(latestRef.current.tfiFileMeta!)) {
      _.defer(() => {
        TFIActions.registerCheckForUpdates(tfiFileMetaId);
      });
    }
    return () => {
      if (BenchlingConfig.ELN_ENABLE_DESKTOP_SYNC) {
        _.defer(() => {
          TFIActions.deregisterCheckForUpdates(tfiFileMetaId);
        });
      }
    };
  }, [tfiFileMetaId]);

  // The sync state is only acted on once it changes after mount: a TFIFileMeta isn't synced automatically on
  // page load in case the upstream change was made a while ago.
  const previousSyncStateRef = useRef<{tfiFileMetaId: number | null; upstreamRev: unknown} | null>(null);
  useEffect(() => {
    const previous = previousSyncStateRef.current;
    previousSyncStateRef.current = {tfiFileMetaId, upstreamRev};
    if (previous == null) {
      return;
    }
    const hadRecentTFIUpdate =
      previous.tfiFileMetaId != null &&
      previous.tfiFileMetaId === tfiFileMetaId &&
      previous.upstreamRev != null &&
      upstreamRev != null &&
      previous.upstreamRev !== upstreamRev;
    if (!hasUpstreamTFIChanges || locked || isCheckingForUpdates) {
      // If there are no updates, we can't update, or a sync is in progress, close any toast.
      _.defer(() => {
        ToastActions.closeToast(tfiNotificationId);
      });
    } else if (hadRecentTFIUpdate) {
      // If an update happened while this attachment was mounted, perform the sync.
      // NB: Avoid flux re-entrancy via action dispatch during render
      _.defer(() => {
        syncTFIFile();
      });
    } else {
      // If there are pending upstream changes, but we didn't pull them in automatically, notify the user.
      _.defer(() => {
        ToastActions.pushToast({
          id: tfiNotificationId,
          type: ToastType.INFO,
          message: `"${fileName}" has updates.`,
          actionText: 'Sync',
          duration: 0,
          onActionClick: () => {
            syncTFIFile();
          },
        });
      });
    }
  }, [
    tfiFileMetaId,
    upstreamRev,
    hasUpstreamTFIChanges,
    locked,
    isCheckingForUpdates,
    fileName,
    tfiNotificationId,
    syncTFIFile,
  ]);

  return {hasUpstreamTFIChanges, isCheckingForUpdates, syncTFIFile};
}
